import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, ChevronRight, MapPin } from "lucide-react";

/**
 * Wallet picker shown right after login when a user has more than one tile
 * linked to their email. They pick ONE tile to scope into, or "All wallets" for
 * the aggregated view. The choice is persisted (localStorage via AuthContext)
 * and a header switcher lets them change it later.
 *
 * Single-wallet users skip this page automatically — see the redirect logic in
 * Login.jsx / the useEffect below.
 */
export default function SelectWallet() {
  const { user, setActiveWalletId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dest = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/my-wallets");
        const list = data?.wallets || [];
        setWallets(list);
        // Single-wallet → auto-select + redirect. Zero wallets → leave the picker
        // up with a friendly empty state (likely an owner-admin who shouldn't
        // see this page anyway).
        if (list.length === 1) {
          setActiveWalletId(list[0].id);
          navigate(dest, { replace: true });
        }
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  const pick = (id) => {
    setActiveWalletId(id);
    navigate(dest, { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="select-wallet-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 pb-20">
        <div className="mb-8 fade-up text-center">
          <div className="pill mb-3 mx-auto"><span className="dot" />Pick a wallet</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-3">
            Welcome back, {user?.name || "there"}
          </h1>
          <p className="text-ink-2 text-sm max-w-xl mx-auto">
            You manage <b className="text-ink">{wallets.length || "multiple"}</b> wallet{(wallets.length || 0) === 1 ? "" : "s"}. Pick which one you want to focus on — everything you see (org chart, KPIs, revenue) will be scoped to that wallet and its downline. You can switch any time from the header.
          </p>
        </div>

        {error && (
          <div className="chip chip-amber mb-6 inline-flex" data-testid="select-wallet-error">{error}</div>
        )}

        <div className="space-y-3" data-testid="wallet-list">
          {loading ? (
            <div className="text-sm text-ink-3 text-center py-10">Loading your wallets…</div>
          ) : wallets.length === 0 ? (
            <div className="glass p-6 text-center">
              <div className="text-sm text-ink-2 mb-3">No wallets are linked to your account yet.</div>
              <button onClick={() => navigate("/dashboard")} className="btn-primary !py-2 !px-4 text-sm" data-testid="select-wallet-skip">
                Continue to Dashboard
              </button>
            </div>
          ) : (
            <>
              {wallets.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => pick(w.id)}
                  data-testid={`wallet-pick-${w.id}`}
                  className="glass w-full p-4 flex items-center gap-4 hover:translate-y-[-1px] transition-transform text-left"
                  style={{ borderRadius: 14 }}
                >
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: w.wallet_kind === "Staking" ? "rgba(194,24,91,0.10)" :
                                  w.wallet_kind === "Both" ? "rgba(124,58,237,0.10)" :
                                  "rgba(64,96,255,0.10)",
                      color: w.wallet_kind === "Staking" ? "#C2185B" :
                             w.wallet_kind === "Both" ? "#7C3AED" :
                             "#4060FF",
                    }}>
                    <Wallet size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="font-semibold text-base text-ink truncate">{w.name}</div>
                      {w.wallet_kind && (
                        <span className="chip text-[11px]"
                          style={{
                            background: w.wallet_kind === "Staking" ? "rgba(194,24,91,0.10)" :
                                        w.wallet_kind === "Both" ? "rgba(124,58,237,0.10)" :
                                        "rgba(64,96,255,0.10)",
                            color: w.wallet_kind === "Staking" ? "#C2185B" :
                                   w.wallet_kind === "Both" ? "#7C3AED" :
                                   "#4060FF",
                          }}>{w.wallet_kind}</span>
                      )}
                      {w.account_status === "invited" && (
                        <span className="chip chip-amber text-[11px]">Invited</span>
                      )}
                    </div>
                    <div className="text-xs text-ink-3 flex items-center gap-3 flex-wrap mt-0.5">
                      {w.wallet_name && (
                        <span className="font-mono">{w.wallet_name}</span>
                      )}
                      {(w.country || w.state) && (
                        <span className="inline-flex items-center gap-1"><MapPin size={11} /> {[w.state, w.country].filter(Boolean).join(", ")}</span>
                      )}
                      {w.amount != null && (
                        <span className="font-mono">${Number(w.amount).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-ink-3 shrink-0" />
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
